import { useMutation, useQuery, type UseMutationOptions } from '@tanstack/react-query';
import {
  getApiV1ContentSyllabusAfoid,
  getApiV1ContentSyllabusAfoidExtended,
  postapiV1ActivityApplication,
  postapiV1ActivityAudioResume,
  postapiV1ActivityBibliography,
  postapiV1ActivityDiagram,
  postapiV1ActivityDiscover,
  postapiV1ActivityExpand,
  postapiV1ActivityExplain,
  postapiV1ActivityExplainSimple,
  postapiV1ActivityFlashcard,
  postapiV1ActivityGlossary,
  postapiV1ActivityObjective,
  postapiV1ActivityPodcast,
  postapiV1ActivityQuizMixed,
  postapiV1ActivityRegulation,
  postapiV1ActivityRemember,
  postapiV1ActivityShortAnswerEvaluate,
  postapiV1ActivityTextResume,
  postapiV1ActivityThread,
  postapiV1ActivityUnderline,
  postapiV1ActivityUuidThreadMessage
} from '../generated/client';
import { activityContextFromParams } from './context';
import { getCurrentScope } from './feature-payload';
import { useAppParams } from './params';

type Awaited<T> = T extends Promise<infer U> ? U : T;
type WithData<T> = Extract<T, { data: unknown }>;
type ResponseOf<F extends (...a: any[]) => any> = NonNullable<
  WithData<Awaited<ReturnType<F>>>['data']
>;

// ── Generic activity factory ────────────────────────────────────────────────
// Most /activity/* endpoints share the same input shape: course_remote_id +
// optional section/module ids + `user` (+ optional `count`).
// This factory reads:
//   • the live AppParams (course/section/module ids, user)
//   • the active scope from `featurePayloadSignal` — set by HomeView when the
//     user clicks a tool inside a scope section.
// and lets activityContextFromParams shape the body accordingly.

function useActivityMutation<TResponse, TExtra extends Record<string, unknown> = Record<string, never>>(
  fn: (opts: any) => Promise<{ data?: TResponse; error?: unknown }>,
  options?: UseMutationOptions<TResponse, Error, TExtra>
) {
  const params = useAppParams();
  return useMutation<TResponse, Error, TExtra>({
    mutationFn: async (extra) => {
      if (!params) throw new Error('Widget not mounted yet');
      const scope = getCurrentScope();
      const ctx = activityContextFromParams(params, scope);
      if (!ctx) {
        throw new Error(`Missing activity context for scope=${scope}`);
      }
      const res = await fn({ body: { ...ctx, ...(extra ?? {}) } });
      if (res.error) throw new Error(JSON.stringify(res.error));
      if (!res.data) throw new Error('Empty response');
      return res.data;
    },
    ...(options ?? {})
  });
}

// ── Per-feature hooks ───────────────────────────────────────────────────────

export const useGenerateFlashcards = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityFlashcard>, { count?: number }>(
    postapiV1ActivityFlashcard as any,
    {}
  );

export const useGenerateQuiz = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityQuizMixed>, { count?: number }>(
    postapiV1ActivityQuizMixed as any
  );

export const useGenerateTextResume = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityTextResume>, { markdown?: boolean }>(
    postapiV1ActivityTextResume as any
  );

export const useGenerateAudioResume = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityAudioResume>>(
    postapiV1ActivityAudioResume as any
  );

export const useGeneratePodcast = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityPodcast>>(
    postapiV1ActivityPodcast as any
  );

export const useGenerateDiagram = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityDiagram>>(
    postapiV1ActivityDiagram as any
  );

export const useGenerateBibliography = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityBibliography>>(
    postapiV1ActivityBibliography as any
  );

export const useGenerateRegulation = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityRegulation>>(
    postapiV1ActivityRegulation as any
  );

export const useGenerateGlossary = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityGlossary>>(
    postapiV1ActivityGlossary as any
  );

export const useGenerateObjectives = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityObjective>>(
    postapiV1ActivityObjective as any
  );

export const useGenerateApplications = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityApplication>>(
    postapiV1ActivityApplication as any
  );

export const useGenerateExpand = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityExpand>>(
    postapiV1ActivityExpand as any
  );

export const useGenerateExplain = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityExplain>>(
    postapiV1ActivityExplain as any
  );

export const useGenerateExplainSimple = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityExplainSimple>>(
    postapiV1ActivityExplainSimple as any
  );

export const useGenerateRemember = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityRemember>>(
    postapiV1ActivityRemember as any
  );

export const useGenerateDiscover = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityDiscover>>(
    postapiV1ActivityDiscover as any
  );

export const useGenerateUnderline = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityUnderline>>(
    postapiV1ActivityUnderline as any
  );

// Evaluates a free-text answer against a model answer using AI. The
// backend grades it `excellent` | `good` | `needs_improvement`.
export const useEvaluateShortAnswer = () =>
  useActivityMutation<
    ResponseOf<typeof postapiV1ActivityShortAnswerEvaluate>,
    { question: string; model_answer: string; student_response: string }
  >(postapiV1ActivityShortAnswerEvaluate as any);

export const useStartThread = () =>
  useActivityMutation<ResponseOf<typeof postapiV1ActivityThread>>(
    postapiV1ActivityThread as any
  );

export const useSendThreadMessage = (threadUuid: string | null) => {
  return useMutation<unknown, Error, { message: string }>({
    mutationFn: async ({ message }) => {
      if (!threadUuid) throw new Error('No thread uuid');
      const res = await postapiV1ActivityUuidThreadMessage({
        path: { uuid: threadUuid },
        body: { message } as any
      });
      if (res.error) throw new Error(JSON.stringify(res.error));
      return res.data;
    }
  });
};

// ── Syllabus queries ────────────────────────────────────────────────────────

export const useSyllabus = (afoId: string | undefined) =>
  useQuery({
    queryKey: ['syllabus', afoId],
    enabled: !!afoId,
    queryFn: async () => {
      const res = await getApiV1ContentSyllabusAfoid({ path: { afoId: afoId! } });
      if (res.error) throw new Error(JSON.stringify(res.error));
      return res.data;
    }
  });

export const useSyllabusExtended = (afoId: string | undefined) =>
  useQuery({
    queryKey: ['syllabus-ext', afoId],
    enabled: !!afoId,
    queryFn: async () => {
      const res = await getApiV1ContentSyllabusAfoidExtended({ path: { afoId: afoId! } });
      if (res.error) throw new Error(JSON.stringify(res.error));
      return res.data;
    }
  });
